import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { PasswordStrength } from "@/components/security/PasswordStrength";
import { passwordMeetsPolicy } from "@/lib/passwordStrength";

// Shown once when a user signed in with a temporary/invite password
// (user_metadata.must_set_password === true). Prompts them to establish a
// permanent password; clears the flag on success.
export function SetPasswordPrompt() {
  const { mustSetPassword, establishPassword } = useAuth();
  const [open, setOpen] = useState(true);
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (!mustSetPassword) return null;

  const canSubmit = passwordMeetsPolicy(next) && next === confirm && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    const { error } = await establishPassword(next);
    setBusy(false);
    if (error) return toast.error(error.message || "We couldn’t set your password. Please try again.");
    toast.success("Your permanent password is set");
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="set-password-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <ShieldCheck className="h-5 w-5 text-accent" /> Set your permanent password
          </DialogTitle>
          <DialogDescription>
            You signed in with a temporary password. Choose a permanent one to secure your account.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="sp-new">New password</Label>
            <Input id="sp-new" type="password" value={next} onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password" data-testid="sp-new" />
            <PasswordStrength value={next} testidPrefix="sp" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sp-confirm">Confirm password</Label>
            <Input id="sp-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password" data-testid="sp-confirm" />
            {confirm && confirm !== next && <p className="text-xs text-destructive">Passwords don’t match yet.</p>}
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="sp-later">Do it later</Button>
            <Button type="submit" disabled={!canSubmit} className="gap-1.5" data-testid="sp-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Set password
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
